import type {ReactElement} from 'react';
import React from 'react';
import {ContextualMenuSliderOptions} from '@Components/contextual-menu/components/contextual-menu-slider-options';
import {useSingleActivePosterItemId} from '@Hooks/poster-editor/useSingleActivePosterItemId';
import {useGetSelectedSlideId} from '@Hooks/poster-editor/useGetSelectedSlideId';
import {useGetKeyFromVideoSlideItem} from '@Hooks/poster-editor/useGetKeyFromVideoSlideItem';

interface VideoSlideItemSpeedOptionProps {
  className?: string;
}

export function VideoSlideItemSpeedOption({className = ''}: VideoSlideItemSpeedOptionProps): ReactElement<any> | null {
  const activeItemId = useSingleActivePosterItemId();
  const activeItem = activeItemId ? window.posterEditor?.whiteboard?.getItemForId(activeItemId) : undefined;
  const activeSlideUID = useGetSelectedSlideId();
  const activeSlide = activeSlideUID && activeItem?.isSlideshow() ? activeItem.getSelectedSlide() : null;
  const speed = useGetKeyFromVideoSlideItem('speed');

  if (!activeItem || !activeSlide || !activeSlideUID) {
    return null;
  }

  if (!activeItem?.isSlideshow()) {
    return null;
  }

  if (!activeSlide?.isVideoSlide()) {
    return null;
  }

  if (speed === undefined) {
    return null;
  }

  const updateVideoSlideSpeed = async (speedValue: number, undoable: boolean): Promise<void> => {
    const newSlideDuration = (activeSlide.endTime - activeSlide.startTime) / speedValue;

    await activeItem.updateSlideFromObject(
      activeSlideUID,
      {
        speed: speedValue,
        slideDuration: newSlideDuration,
      },
      {undoable, checkForDurationUpdate: undoable}
    );
  };

  const onSpeedEndHandler = async (val: number): Promise<void> => {
    await updateVideoSlideSpeed(val, true);
  };

  const getSpeedControlSlider = (): ReactElement<any> => {
    return (
      <ContextualMenuSliderOptions
        id="video-slide-speed-option"
        className={className}
        range={{
          min: 0.5,
          max: 2,
        }}
        transformDisplay={{
          min: 0.5,
          max: 2,
        }}
        step={0.25}
        value={speed}
        label={window.i18next.t('pmwjs_speed')}
        onChange={(val: number): void => {
          void updateVideoSlideSpeed(val, false);
        }}
        onChangeEnded={(val: number): void => {
          void onSpeedEndHandler(val);
        }}
      />
    );
  };

  return getSpeedControlSlider();
}
